import React, { useState, useEffect } from "react";
import { Box, Grid, useMediaQuery, CircularProgress } from "@mui/material";
import { ref, get } from "firebase/database";

import { rtdb } from "../services/firebase";
import ProdutosHeader from "../components/ProdutosHeader";
import FiltrosSidebar from "../components/FiltrosSidebar";
import ProductCard from "../components/ProductCard";

export default function Produtos() {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [produtos, setProdutos] = useState([]);
    const [produtosFiltrados, setProdutosFiltrados] = useState([]);
    const [produtosOrdenados, setProdutosOrdenados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ordenacao, setOrdenacao] = useState("relevancia");
    const [filtros, setFiltros] = useState({
        preco: [0, 500],
        categorias: [],
        promocoes: false,
        emEstoque: false,
    });

    // Detecta mobile
    const isMobile = useMediaQuery("(max-width: 900px)");

    useEffect(() => {
        carregarProdutos();
    }, []);

    useEffect(() => {
        aplicarFiltros();
    }, [produtos, filtros]);

    useEffect(() => {
        aplicarOrdenacao();
    }, [produtosFiltrados, ordenacao]);

    async function carregarProdutos() {
        try {
            const productsRef = ref(rtdb, "products");
            const snapshot = await get(productsRef);

            if (!snapshot.exists()) {
                setProdutos([]);
                return;
            }

            const data = snapshot.val();
            const produtosLista = Object.keys(data).map((id) => ({
                id,
                ...data[id],
            }));

            setProdutos(produtosLista);
            setProdutosFiltrados(produtosLista);
            setProdutosOrdenados(produtosLista);
        } catch (err) {
            console.error("Erro ao carregar produtos:", err);
            setProdutos([]);
            setProdutosFiltrados([]);
            setProdutosOrdenados([]);
        } finally {
            setLoading(false);
        }
    }

    // Função auxiliar para converter preço string para número
    function converterPreco(precoStr) {
        if (!precoStr) return 0;
        // Remove tudo exceto números, vírgula e ponto
        const limpo = precoStr.toString().replace(/[^\d,.]/g, "");
        // Substitui vírgula por ponto para parseFloat
        const numero = parseFloat(limpo.replace(",", "."));
        return isNaN(numero) ? 0 : numero;
    }

    function aplicarFiltros() {
        let filtrados = [...produtos];

        // Filtro por preço
        filtrados = filtrados.filter((produto) => {
            const precoAtual = produto.precos?.isPromo && produto.precos?.promo
                ? converterPreco(produto.precos.promo)
                : converterPreco(produto.precos?.normal);
            
            return precoAtual >= filtros.preco[0] && precoAtual <= filtros.preco[1];
        });

        // Filtro por categorias
        if (filtros.categorias.length > 0) {
            filtrados = filtrados.filter((produto) =>
                filtros.categorias.includes(produto.categoria)
            );
        }

        // Filtro por promoções
        if (filtros.promocoes) {
            filtrados = filtrados.filter((produto) => produto.precos?.isPromo === true);
        }

        // Filtro por estoque
        if (filtros.emEstoque) {
            filtrados = filtrados.filter((produto) => {
                // Verifica se tem variações com quantidade > 0
                if (produto.variacoes && Array.isArray(produto.variacoes)) {
                    return produto.variacoes.some((v) => {
                        const qtd = parseInt(v.quantidade || "0", 10);
                        return qtd > 0;
                    });
                }
                return false;
            });
        }

        setProdutosFiltrados(filtrados);
    }

    function aplicarOrdenacao() {
        let ordenados = [...produtosFiltrados];

        switch (ordenacao) {
            case "preco_menor":
                ordenados.sort((a, b) => {
                    const precoA = a.precos?.isPromo && a.precos?.promo
                        ? converterPreco(a.precos.promo)
                        : converterPreco(a.precos?.normal);
                    const precoB = b.precos?.isPromo && b.precos?.promo
                        ? converterPreco(b.precos.promo)
                        : converterPreco(b.precos?.normal);
                    return precoA - precoB;
                });
                break;

            case "preco_maior":
                ordenados.sort((a, b) => {
                    const precoA = a.precos?.isPromo && a.precos?.promo
                        ? converterPreco(a.precos.promo)
                        : converterPreco(a.precos?.normal);
                    const precoB = b.precos?.isPromo && b.precos?.promo
                        ? converterPreco(b.precos.promo)
                        : converterPreco(b.precos?.normal);
                    return precoB - precoA;
                });
                break;

            case "novidades":
                ordenados.sort((a, b) => {
                    const dataA = a.createdAt || 0;
                    const dataB = b.createdAt || 0;
                    return dataB - dataA; // Mais recente primeiro
                });
                break;

            case "relevancia":
            default:
                // Mantém a ordem original (ordem de carregamento)
                break;
        }

        setProdutosOrdenados(ordenados);
    }

    function handleFiltersChange(novosFiltros) {
        setFiltros(novosFiltros);
    }

    function handleSortChange(novaOrdenacao) {
        setOrdenacao(novaOrdenacao);
    }

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100vh",
                    backgroundColor: "#f5f5f5",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                minHeight: "100vh",
                backgroundColor: "#f5f5f5",
            }}
        >
            {/* SIDEBAR — DESKTOP APENAS */}
            {!isMobile && (
                <FiltrosSidebar 
                    produtos={produtos}
                    onFiltersChange={handleFiltersChange}
                />
            )}

            {/* CONTEÚDO */}
            <Box
                sx={{
                    flexGrow: 1,
                    p: isMobile ? 2 : 3,
                    pb: 8,
                }}
            >
                <ProdutosHeader
                    isMobile={isMobile}
                    openDrawer={() => setDrawerOpen(true)}
                    onSortChange={handleSortChange}
                />

                {/* GRID */}
                {produtos.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "400px",
                        }}
                    >
                        <Box sx={{ textAlign: "center", color: "#999" }}>
                            Nenhum produto encontrado
                        </Box>
                    </Box>
                ) : produtosOrdenados.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: "400px",
                        }}
                    >
                        <Box sx={{ textAlign: "center", color: "#999" }}>
                            Nenhum produto corresponde aos filtros selecionados
                        </Box>
                    </Box>
                ) : (
                    <Grid
                        container
                        spacing={isMobile ? 2 : 3}
                        sx={{
                            justifyContent: isMobile ? "center" : "flex-start",
                        }}
                    >
                        {produtosOrdenados.map((item) => (
                            <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                lg={3}
                                key={item.id}
                                sx={{ display: "flex" }}
                            >
                                <Box sx={{ width: "100%" }}>
                                    <ProductCard product={item} />
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {/* DRAWER MOBILE */}
            {isMobile && (
                <FiltrosSidebar
                    mobile
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    produtos={produtos}
                    onFiltersChange={handleFiltersChange}
                />
            )}
        </Box>
    );
}
